import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@src/lib/utils/cn";

/**
 * A square tile holding one icon: a type marker in a header row, a choice in
 * a tray of tiles, the subject of a small popover. Not a button; put it
 * inside one when it's pressable.
 */
const iconTileVariants = cva(
  // [&_svg]:text-current: icons that carry their own colour take the tone.
  "inline-flex shrink-0 items-center justify-center [&_svg]:shrink-0 [&_svg]:text-current",
  {
    variants: {
      size: {
        // 20px tile, 14px icon: beside 12–14px text in a header row.
        sm: "size-5 rounded-md [&_svg]:size-3.5",
        // 24px tile, 16px icon: beside 16px text.
        md: "size-6 rounded-md [&_svg]:size-4",
        // 40px tile, 20px icon: a tray choice or a popover's subject.
        lg: "size-10 rounded-xl [&_svg]:size-5",
      },
      tone: {
        neutral: "bg-action-secondary text-icon-secondary",
        // On a white surface, a softer grey than the translucent overlay.
        subtle: "bg-bg-secondary text-icon-primary",
        brand: "bg-brand-bg text-brand-text",
      },
    },
    defaultVariants: { size: "sm", tone: "neutral" },
  },
);

type IconTileProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof iconTileVariants>;

const IconTile = React.forwardRef<HTMLSpanElement, IconTileProps>(
  ({ className, size, tone, ...props }, ref) => (
    <span
      ref={ref}
      aria-hidden
      className={cn(iconTileVariants({ size, tone }), className)}
      {...props}
    />
  ),
);
IconTile.displayName = "IconTile";

export { IconTile, iconTileVariants };
